import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { translations, SUPPORTED_LANGS } from '@/i18n/translations';

const STORAGE_KEY = 'stc-marine-lang';

const I18nContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (path) => path,
});

const detectInitialLang = () => {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && translations[stored]) return stored;
  const browser = (navigator.language || 'en').toLowerCase().split('-')[0];
  if (translations[browser]) return browser;
  return 'en';
};

// Resolve a dotted path against an object, e.g. "hero.title"
const resolve = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
};

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitialLang);

  const setLang = useCallback((next) => {
    if (!translations[next]) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const t = useCallback(
    (path, fallback) => {
      const value = resolve(translations[lang], path);
      if (value !== undefined && value !== null) return value;
      const enValue = resolve(translations.en, path);
      return enValue !== undefined ? enValue : fallback !== undefined ? fallback : path;
    },
    [lang]
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t, supported: SUPPORTED_LANGS }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
